import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Award,
  Building2,
  Globe,
  Hash,
  Dumbbell,
  FileText,
  Video,
  Plus,
  Upload,
  RefreshCw,
  CreditCard,
  AlertCircle,
  Check,
  Loader,
  Clock,
  Layers
} from "lucide-react";
import { GetUserAdminDetails, UploadPDF, UploadVideo, CreateMasterDataAndAssign } from "../../Api"; // New comprehensive API
import { useEffect, useState } from "react";

export default function Modal({ user, onClose }) {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [formData, setFormData] = useState({}); // Store both UserDetails and UserActivity
  const [pdfAssignments, setPdfAssignments] = useState([]);
  const [videoAssignments, setVideoAssignments] = useState([]);
  const [dietChartAssignments, setDietChartAssignments] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentType, setAssignmentType] = useState('pdf'); // 'pdf' or 'video'
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    categories: '',
    file: null
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [loading, setLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState(null);

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  useEffect(() => {
    console.log("UserID", user.userId);

    // Fetch comprehensive user details using the new API
    GetUserAdminDetails(user.userId)
      .then((result) => {
        console.log("Comprehensive user details:", result.data);
        const userData = result.data.data;

        // Set basic user information
        const basicInfo = {
          userId: userData.userId,
          username: userData.username,
          email: userData.email,
          mobileNumber: userData.mobileNumber,
          firstName: userData.firstName,
          lastName: userData.lastName,
          status: userData.status,
          role: userData.roleName,
          emailVerified: userData.emailVerified,
          mobileVerified: userData.mobileVerified,
          profileImageUrl: userData.profileImageUrl,
          createdAt: userData.createdAt,
          modifiedAt: userData.modifiedAt,
          lastLoginAt: userData.lastLoginAt
        };

        // Set activity details if available
        const activityInfo = userData.activityDetails ? {
          age: userData.activityDetails.age,
          gender: userData.activityDetails.gender,
          height: userData.activityDetails.height,
          weight: userData.activityDetails.weight,
          goal: userData.activityDetails.goal,
          activityLevel: userData.activityDetails.activityLevel,
          exerciseDays: userData.activityDetails.exerciseDays,
          sleepHours: userData.activityDetails.sleepHours,
          waterIntake: userData.activityDetails.waterIntake,
          dietType: userData.activityDetails.dietType,
          dietaryRestrictions: userData.activityDetails.dietaryRestrictions,
          mealsPerDay: userData.activityDetails.mealsPerDay,
          alcoholConsumption: userData.activityDetails.alcoholConsumption
        } : {};

        // Set subscription and order data
        const subscriptionInfo = {
          subscriptions: userData.subscriptions || [],
          orders: userData.orders || [],
          addonSubscriptions: userData.addonSubscriptions || [],
          sessionSubscription: userData.sessionSubscription || [],
          invoices: userData.invoices || [],
          summaryStats: userData.summaryStats || {}
        };

        // Set assignment data from the admin details API
        setPdfAssignments(userData.pdfAssignments || []);
        setVideoAssignments(userData.videoAssignments || []);
        setDietChartAssignments(userData.dietChartAssignments || []);

        setFormData({ ...basicInfo, ...activityInfo, ...subscriptionInfo });
      })
      .catch((error) => {
        console.error("Error fetching user details:", error);
      });
  }, [user]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value || "",
    }));
  };

  const handleSave = () => {
    setIsEditing(false);
    // Add your save logic here (e.g., API call to update UserDetails and UserActivity)
    console.log("Saved Data:", formData);
  };

  const fetchUserAssignments = async () => {
    try {
      const result = await GetUserAdminDetails(user.userId);
      const userData = result.data.data;
      setPdfAssignments(userData.pdfAssignments || []);
      setVideoAssignments(userData.videoAssignments || []);
      setDietChartAssignments(userData.dietChartAssignments || []);
    } catch (error) {
      console.error('Error fetching user assignments:', error);
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dataType = assignmentType === 'pdf' ? 'PDF_DOCUMENT' : 'VIDEO_CONTENT';

      await CreateMasterDataAndAssign(
        user.userId,
        assignmentForm.title,
        assignmentForm.description,
        assignmentForm.categories,
        assignmentForm.file,
        dataType
      );

      // Reset form and close modal
      setAssignmentForm({ title: '', description: '', categories: '', file: null });
      setAssignmentError(null);
      setShowAssignmentModal(false);

      // Refresh assignments
      await fetchUserAssignments();

      // alert(`${assignmentType.toUpperCase()} assignment created and assigned successfully!`);
      showToast(`${assignmentType.toUpperCase()} assignment created and assigned successfully!`);
    } catch (error) {
      console.error(`Error creating ${assignmentType} assignment:`, error);
      // alert(`Error creating ${assignmentType} assignment. Please try again.`);
      showToast(`Error creating ${assignmentType} assignment. Please try again.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (assignmentType === 'video' && file.size > MAX_FILE_SIZE) {
        setAssignmentError(`Video too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB (Max 100MB)`);
        e.target.value = '';
        setAssignmentForm({ ...assignmentForm, file: null });
        return;
      }
      setAssignmentError(null);
      setAssignmentForm({
        ...assignmentForm,
        file: file
      });
    }
  };

  const InputField = ({ icon: Icon, label, name, type = "text", value, readOnly }) => (
    <div className="relative group transition-all duration-300 hover:scale-[1.02]">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500" />
      </div>
      <input
        type={type}
        name={name}
        value={value || "-"}
        onChange={handleChange}
        readOnly={true}
        className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl
                 bg-white/80 backdrop-blur-sm shadow-sm
                 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                 transition-all duration-200 ease-in-out
                 text-gray-800 placeholder-gray-400
                 disabled:bg-gray-50 disabled:text-gray-500
                 hover:shadow-md"
        placeholder={label}
      />
      <label className="absolute left-10 -top-2.5 text-xs font-medium bg-gradient-to-r from-blue-600 to-purple-600 
                       bg-clip-text text-transparent px-1 py-0.5 rounded-full backdrop-blur-sm">
        {label}
      </label>
    </div>
  );

  return (
    <AnimatePresence>
      {user && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Toast Notification */}
          {toast.show && (
            <div className={`fixed top-4 right-4 z-[60] p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
              }`}>
              <div className="flex items-center gap-2">
                {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                {toast.message}
              </div>
            </div>
          )}
          <motion.div
            className="relative w-full max-w-4xl h-[80vh] mx-4 overflow-hidden rounded-2xl"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-white/80 backdrop-blur-xl" />
            <div className="relative p-6 h-full overflow-y-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    User Information
                  </h2>
                </div>
                <div className="flex items-center gap-4">
                  {/* {isEditing ? (
                    <>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg
                                 hover:shadow-lg transition-all duration-300"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg
                                 hover:bg-gray-200 transition-all duration-300"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg
                               hover:bg-blue-100 transition-all duration-300"
                    >
                      Edit Profile
                    </button>
                  )} */}
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 hover:bg-gray-100 transition-colors"
                  >
                    <svg
                      className="w-6 h-6 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex space-x-2 mb-6 border-b border-gray-200">
                {["personal", "contact", "activity", "subscriptions", "addon plans", "addon sessions", "orders", "assignments"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-2 px-2 text-xs font-medium transition-all duration-300
                      ${activeTab === tab
                        ? "text-blue-600 border-b-2 border-blue-600 scale-105"
                        : "text-gray-500 hover:text-gray-700"
                      }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activeTab === "personal" && (
                  <>
                    <InputField
                      icon={User}
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Username"
                      name="username"
                      value={formData.username}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={Award}
                      label="Role"
                      name="role"
                      value={formData.role}
                      readOnly
                    />
                    <InputField
                      icon={Calendar}
                      label="Age"
                      name="age"
                      type="text"
                      value={formData.age}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={Globe}
                      label="Gender"
                      name="gender"
                      value={formData.gender}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={Calendar}
                      label="Account Created"
                      name="createdAt"
                      value={formData.createdAt ? new Date(formData.createdAt).toLocaleDateString() : '-'}
                      readOnly
                    />
                    {/* <InputField
                      icon={Calendar}
                      label="Last Login"
                      name="lastLoginAt"
                      value={formData.lastLoginAt ? new Date(formData.lastLoginAt).toLocaleDateString() : '-'}
                      readOnly
                    /> */}
                    <InputField
                      icon={User}
                      label="Email Verified"
                      name="emailVerified"
                      value={formData.emailVerified ? 'Yes' : 'No'}
                      readOnly
                    />
                    <InputField
                      icon={Phone}
                      label="Mobile Verified"
                      name="mobileVerified"
                      value={formData.mobileVerified ? 'Yes' : 'No'}
                      readOnly
                    />
                    <InputField
                      icon={User}
                      label="Account Status"
                      name="status"
                      value={formData.status ? 'Active' : 'Inactive'}
                      readOnly
                    />
                  </>
                )}
                {activeTab === "contact" && (
                  <>
                    <InputField
                      icon={Mail}
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={Phone}
                      label="Mobile Number"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      readOnly={!isEditing}
                    />
                  </>
                )}
                {activeTab === "activity" && (
                  <>
                    <InputField
                      icon={Dumbbell}
                      label="Activity Level"
                      name="activityLevel"
                      value={formData.activityLevel}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={Calendar}
                      label="Exercise Days"
                      name="exerciseDays"
                      value={formData.exerciseDays}
                      readOnly={!isEditing}
                    />
                    {/* <InputField
                      icon={Dumbbell}
                      label="Workout Preferences"
                      name="workoutPreferences"
                      value={formData.workoutPreferences}
                      readOnly={!isEditing}
                    /> */}
                    {/* <InputField
                      icon={MapPin}
                      label="Workout Location"
                      name="workoutLocation"
                      value={formData.workoutLocation}
                      readOnly={!isEditing}
                    /> */}
                    {/* <InputField
                      icon={Calendar}
                      label="Workout Time"
                      name="workoutTime"
                      value={formData.workoutTime}
                      readOnly={!isEditing}
                    /> */}
                    <InputField
                      icon={Award}
                      label="Goal"
                      name="goal"
                      value={formData.goal}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Diet Type"
                      name="dietType"
                      value={formData.dietType}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={Hash}
                      label="Meals Per Day"
                      name="mealsPerDay"
                      value={formData.mealsPerDay}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Height"
                      name="height"
                      value={formData.height}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Weight"
                      name="weight"
                      value={formData.weight}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Health Conditions"
                      name="healthConditions"
                      value={formData.healthConditions}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Sleep Hours"
                      name="sleepHours"
                      value={formData.sleepHours}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Water Intake"
                      name="waterIntake"
                      value={formData.waterIntake}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Dietary Restrictions"
                      name="dietaryRestrictions"
                      value={formData.dietaryRestrictions}
                      readOnly={!isEditing}
                    />
                    <InputField
                      icon={User}
                      label="Alcohol Consumption"
                      name="alcoholConsumption"
                      value={formData.alcoholConsumption}
                      readOnly={!isEditing}
                    />
                  </>
                )}
                {activeTab === "subscriptions" && (
                  <div className="col-span-2">
                    {/* <h3 className="text-lg font-semibold mb-4 text-gray-800">
                      Subscription Information
                    </h3> */}

                    <div className="max-h-[40vh] overflow-y-auto space-y-4 pr-2">
                      {(() => {
                        const activeSubscriptions =
                          formData.subscriptions?.filter(
                            (subscription) => subscription.isActive
                          ) || [];

                        return activeSubscriptions.length > 0 ? (
                          activeSubscriptions.map((subscription, index) => (
                            <div
                              key={index}
                              className="bg-white p-6 rounded-xl border border-blue-200 bg-blue-50/10 shadow-sm hover:shadow-md transition-all duration-300"
                            >
                              {/* Header */}
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <h4 className="text-xl font-bold text-gray-800">
                                    {subscription.planName || "-"}
                                  </h4>
                                  <div className="flex items-center mt-1">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                      Active
                                    </span>
                                  </div>
                                </div>

                                <div className="text-right">
                                  <p className="text-2xl font-bold text-blue-600">
                                    ₹{subscription.price || "-"}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {subscription.timePeriod || "-"}
                                  </p>
                                </div>
                              </div>

                              {/* Details */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div className="flex items-center">
                                  <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                                  <div>
                                    <p className="text-xs text-gray-500">Start Date</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {subscription.startDate
                                        ? new Date(
                                          subscription.startDate
                                        ).toLocaleDateString()
                                        : "-"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                                  <div>
                                    <p className="text-xs text-gray-500">End Date</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {subscription.endDate
                                        ? new Date(
                                          subscription.endDate
                                        ).toLocaleDateString()
                                        : "-"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <RefreshCw className="w-5 h-5 mr-2 text-blue-500" />
                                  <div>
                                    <p className="text-xs text-gray-500">Auto Renewal</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {subscription.autoRenewal ? "Enabled" : "Disabled"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <Hash className="w-5 h-5 mr-2 text-blue-500" />
                                  <div>
                                    <p className="text-xs text-gray-500">Subscription ID</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {subscription.subscriptionId || "-"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-12">
                            <div className="bg-gray-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                              <CreditCard className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">No active subscriptions found</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {activeTab === "addon plans" && (
                  <div className="col-span-2">
                    {/* <h3 className="text-lg font-semibold mb-4 text-gray-800">Addon Plans</h3> */}
                    <div className="max-h-[50vh] overflow-y-auto space-y-4 pr-2">
                      {formData.addonSubscriptions && formData.addonSubscriptions.length > 0 ? (
                        [...formData.addonSubscriptions]
                          .sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1))
                          .map((addon, index) => (
                            <div key={index} className={`bg-white p-6 rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 ${addon.isActive
                              ? 'border-blue-200 bg-blue-50/10'
                              : 'border-red-200 bg-red-50/10'
                              }`}>
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <h4 className={`text-xl font-bold ${addon.isActive ? 'text-gray-800' : 'text-red-800'}`}>
                                    {addon.addonPlanName || '-'}
                                  </h4>
                                  <div className="flex items-center mt-1">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${addon.status === 'ACTIVE' || addon.isActive
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                      }`}>
                                      {addon.status || (addon.isActive ? 'ACTIVE' : 'INACTIVE')}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className={`text-2xl font-bold ${addon.isActive ? 'text-blue-600' : 'text-red-600'}`}>
                                    ₹{addon.price || '-'}
                                  </p>
                                  <p className="text-sm text-gray-500">{addon.durationDays ? `${addon.durationDays} Days` : '-'}</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div className="flex items-center">
                                  <Calendar className={`w-5 h-5 mr-2 ${addon.isActive ? 'text-blue-500' : 'text-red-500'}`} />
                                  <div>
                                    <p className="text-xs text-gray-500">Start Date</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {addon.startDate ? new Date(addon.startDate).toLocaleDateString() : '-'}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <Calendar className={`w-5 h-5 mr-2 ${addon.isActive ? 'text-blue-500' : 'text-red-500'}`} />
                                  <div>
                                    <p className="text-xs text-gray-500">End Date</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {addon.endDate ? new Date(addon.endDate).toLocaleDateString() : '-'}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  {/* <Dumbbell className={`w-5 h-5 mr-2 ${addon.isActive ? 'text-blue-500' : 'text-red-500'}`} /> */}
                                  <div>
                                    <p className="text-xs text-gray-500">Category</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {addon.category}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <RefreshCw className={`w-5 h-5 mr-2 ${addon.isActive ? 'text-blue-500' : 'text-red-500'}`} />
                                  <div>
                                    <p className="text-xs text-gray-500">Auto Renewal</p>
                                    <p className="text-sm font-medium text-gray-800">
                                      {addon.autoRenewal ? 'Enabled' : 'Disabled'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                      ) : (
                        <div className="text-center py-12">
                          <div className="bg-gray-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <CreditCard className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 text-center py-8">No addon plans found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "addon sessions" && (
                  <div className="col-span-2">
                    {/* <h3 className="text-lg font-semibold mb-4 text-gray-800">
      Addon Sessions
    </h3> */}

                    <div className="max-h-[50vh] overflow-y-auto space-y-4 pr-2">
                      {formData.sessionSubscription?.length > 0 ? (
                        formData.sessionSubscription.map((addon) => (
                          <div
                            key={addon.id}
                            className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                          >
                            {/* Header */}
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-xl font-bold text-gray-800">
                                  {addon.addonSessionName}
                                </h4>
                              </div>

                              <div className="text-right">
                                <p className="text-2xl font-bold text-blue-600">
                                  ₹{addon.price}
                                </p>
                                {/* <p className="text-xs text-gray-500 flex items-center justify-end gap-1">
                  <Layers className="w-4 h-4 text-blue-500" />
                  {addon.sessionCount} Sessions
                </p> */}
                              </div>
                            </div>

                            {/* Description */}
                            {addon.description && (
                              <p className="text-sm text-gray-600 mb-4">
                                {addon.description}
                              </p>
                            )}

                            {/* Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-blue-500" />
                                <div>
                                  <p className="text-gray-500">Duration per Session</p>
                                  <p className="font-medium text-gray-800">
                                    {addon.durationMinutes} Minutes
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <Layers className="w-5 h-5 text-blue-500" />
                                <div>
                                  <p className="text-gray-500">Total Sessions</p>
                                  <p className="font-medium text-gray-800">
                                    {addon.sessionCount}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-blue-500" />
                                <div>
                                  <p className="text-gray-500">Created At</p>
                                  <p className="font-medium text-gray-800">
                                    {new Date(addon.createdAt).toLocaleString()}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-blue-500" />
                                <div>
                                  <p className="text-gray-500">Last Updated</p>
                                  <p className="font-medium text-gray-800">
                                    {new Date(addon.modifiedAt).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          No addon sessions found
                        </div>
                      )}
                    </div>
                  </div>
                )}



                {activeTab === "orders" && (
                  <div className="col-span-2">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Order History & Statistics</h3>

                    {/* Summary Statistics */}
                    {formData.summaryStats && (
                      <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="text-md font-semibold mb-3 text-blue-800">Summary Statistics</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-blue-600">{formData.summaryStats.totalSubscriptions || 0}</p>
                            <p className="text-sm text-gray-600">Total Subscriptions</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-green-600">{formData.summaryStats.activeSubscriptions || 0}</p>
                            <p className="text-sm text-gray-600">Active Subscriptions</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-purple-600">{formData.summaryStats.totalOrders || 0}</p>
                            <p className="text-sm text-gray-600">Total Orders</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-orange-600">₹{formData.summaryStats.totalSpent || 0}</p>
                            <p className="text-sm text-gray-600">Total Spent</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="max-h-[40vh] overflow-y-auto space-y-4 pr-2">
                      {formData.orders && formData.orders.length > 0 ? (
                        formData.orders.map((order, index) => (
                          <div key={index} className="bg-white p-6 rounded-xl border border-green-200 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-xl font-bold text-gray-800">{order.productName || '-'}</h4>
                                <div className="flex items-center mt-1 space-x-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    {order.orderStatus || '-'}
                                  </span>
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                    {order.paymentStatus || '-'}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-600">₹{order.totalPrice || '-'}</p>
                                <p className="text-sm text-gray-500">
                                  Order #{typeof order.orderId === 'string' ? order.orderId.substring(0, 8) : order.orderId || '-'}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div className="flex items-center">
                                <Hash className="w-5 h-5 text-green-500 mr-2" />
                                <div>
                                  <p className="text-xs text-gray-500">Order ID</p>
                                  <p className="text-sm font-medium text-gray-800">
                                    {order.orderId || '-'}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center">
                                <Calendar className="w-5 h-5 text-green-500 mr-2" />
                                <div>
                                  <p className="text-xs text-gray-500">Order Date</p>
                                  <p className="text-sm font-medium text-gray-800">
                                    {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '-'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <div className="bg-gray-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <CreditCard className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 text-center py-8">No orders found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "assignments" && (
                  <div className="col-span-2">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-semibold text-gray-800">User Assignments</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setAssignmentType('pdf');
                            setAssignmentError(null);
                            setShowAssignmentModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <FileText className="w-4 h-4" />
                          Assign Document
                        </button>
                        <button
                          onClick={() => {
                            setAssignmentType('video');
                            setAssignmentError(null);
                            setShowAssignmentModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Video className="w-4 h-4" />
                          Assign Video
                        </button>
                      </div>
                    </div>

                    <div className="max-h-[40vh] overflow-y-auto pr-2">
                      {/* PDF Assignments */}
                      <div className="mb-8">
                        <h4 className="text-md font-semibold mb-4 text-gray-700 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-red-600" />
                          Document Assignments ({pdfAssignments.length})
                        </h4>
                        <div className="space-y-4">
                          {pdfAssignments.length > 0 ? (
                            pdfAssignments.map((assignment, index) => (
                              <div key={index} className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">Title</label>
                                    <p className="text-gray-800">{assignment.title || '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">Categories</label>
                                    <p className="text-gray-800">{assignment.categories || '-'}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-600">Description</label>
                                    <p className="text-gray-800">{assignment.description || '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">Created Date</label>
                                    <p className="text-gray-800">{assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString() : '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">File Name</label>
                                    <p className="text-gray-800 text-sm break-all">{assignment.fileName || '-'}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-center py-8">No Document assignments found</p>
                          )}
                        </div>
                      </div>

                      {/* Video Assignments */}
                      <div>
                        <h4 className="text-md font-semibold mb-4 text-gray-700 flex items-center gap-2">
                          <Video className="w-5 h-5 text-blue-600" />
                          Video Assignments ({videoAssignments.length})
                        </h4>
                        <div className="space-y-4">
                          {videoAssignments.length > 0 ? (
                            videoAssignments.map((assignment, index) => (
                              <div key={index} className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">Title</label>
                                    <p className="text-gray-800">{assignment.title || '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">Categories</label>
                                    <p className="text-gray-800">{assignment.categories || '-'}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-600">Description</label>
                                    <p className="text-gray-800">{assignment.description || '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">Created Date</label>
                                    <p className="text-gray-800">{assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString() : '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-600">File Name</label>
                                    <p className="text-gray-800 text-sm break-all">{assignment.fileName || '-'}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-center py-8">No video assignments found</p>
                          )}
                        </div>
                      </div>

                      {/* Diet Chart Assignments */}
                      <div className="mt-8">
                        <h4 className="text-md font-semibold mb-4 text-gray-700 flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Diet Chart Assignments ({dietChartAssignments.length})
                        </h4>
                        <div className="space-y-4">
                          {dietChartAssignments.length > 0 ? (
                            dietChartAssignments.map((assignment, index) => (
                              <div key={index} className={`p-4 rounded-lg border ${assignment.isActive
                                ? 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200'
                                }`}>
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Diet Chart Name</label>
                                    <p className="text-gray-800 font-bold">{assignment.dietChartName || '-'}</p>
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Assigned By</label>
                                    <p className="text-gray-800 font-medium">{assignment.assignedBy || assignment.assignmentType || 'SYSTEM'}</p>
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Date</label>
                                    <p className="text-gray-800 font-medium">{assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString() : '-'}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-center py-8">No diet chart assignments found</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-2xl mx-4 overflow-hidden rounded-2xl"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {loading && (
              <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
                <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-lg font-semibold text-gray-800">
                  Uploading {assignmentType === 'pdf' ? 'Document' : 'Video'}...
                </p>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-white/80 backdrop-blur-xl" />
            <div className="relative p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Create {assignmentType === 'pdf' ? 'Document' : 'Video'} Assignment
                </h3>
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAssignmentSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={assignmentForm.title}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={assignmentForm.description}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
                  <input
                    type="text"
                    value={assignmentForm.categories}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, categories: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., fitness, nutrition, workout"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {assignmentType === 'pdf' ? 'Document' : 'Video'} File
                  </label>
                  <input
                    type="file"
                    accept={assignmentType === 'pdf' ? '.pdf' : 'video/*'}
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  {assignmentType === 'pdf' && (
                    <p className="text-red-500 text-xs mt-1">Only PDF format documents are accepted.</p>
                  )}
                  {assignmentForm.file && (
                    <p className="text-red-600 text-xs mt-1 font-medium">{assignmentError}</p>
                  )}
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentModal(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!!assignmentError || loading}
                    className={`px-4 py-2 text-white rounded-lg transition-colors ${assignmentError ? 'bg-gray-400 cursor-not-allowed' : (assignmentType === 'pdf' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700')}`}
                  >
                    Create Assignment
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}